<template>
  <div class="home-view">
    <section class="home-hero wrap">
      <div class="hero-copy">
        <span class="eyebrow">Tattoostudio in Kreuzlingen</span>
        <h1>White Pearl <em>Ink</em></h1>
        <p>
          Fineline, Light Blackwork & Kunstwerke von Rebecca Sophia. Inspiriert von Natur und
          Fantasy.
        </p>
        <div class="action-row">
          <router-link to="/tattoos" class="button button-primary"
            >Tattoos entdecken <InkIcon name="arrow" /></router-link
          ><router-link to="/art" class="text-link"
            >Zur Kunst <InkIcon name="diagonal"
          /></router-link>
        </div>
        <router-link to="/about-me" class="hero-signoff"
          ><img v-if="portrait" :src="portrait" width="42" height="42" alt="Rebecca Sophia" /><span
            >Rebecca Sophia<small>Künstlerin & Tätowiererin</small></span
          ></router-link
        >
      </div>
      <div class="hero-composition">
        <img
          class="hero-tattoo"
          :src="heroTattoo"
          width="600"
          height="800"
          alt="Filigranes Tarot-Tattoo und Schmetterling von Rebecca Sophia"
          fetchpriority="high"
        />
        <figure class="hero-art">
          <img
            :src="heroArt"
            width="240"
            height="320"
            alt="Fantasievolle Natur- und Seelenkunst von Rebecca"
            fetchpriority="high"
          />
          <figcaption>Soul Art</figcaption>
        </figure>
        <InkIcon class="hero-spark" />
        <InkIcon class="hero-spark hero-spark-small" />
        <div class="hero-seal" aria-hidden="true">
          <img :src="logo" width="54" height="40" alt="" />
          <span class="hero-seal-name">White Pearl<br />Ink</span>
        </div>
        <span class="hero-side-note">Kreuzlingen · Schweiz</span>
      </div>
    </section>
    <div class="craft-strip">
      <div class="wrap">
        <span>Fineline Tattoos</span><InkIcon /><span>Light Blackwork</span><InkIcon /><span
          >Soul Art</span
        ><InkIcon /><span>Custom Designs</span><InkIcon /><span>Acryl Paintings</span>
      </div>
    </div>
    <section class="section wrap">
      <div class="section-heading">
        <div>
          <h2>Tattoos & Kunstwerke</h2>
        </div>
      </div>
      <div class="world-grid">
        <router-link to="/tattoos" class="world-card">
          <div class="world-image">
            <img
              :src="tattooImages[1]?.src || heroTattoo"
              width="360"
              height="480"
              alt="Individuelles Fineline-Tattoo"
              loading="lazy"
            />
          </div>
          <div class="world-copy">
            <span class="eyebrow">01 / Body Art</span>
            <h3>Tattoos</h3>
            <p>Fineline und Blackwork als Custom Design oder Wanna Do's unter deiner Haut.</p>
            <span class="text-link">Mehr entdecken <InkIcon name="diagonal" /></span>
          </div>
        </router-link>
        <router-link to="/art" class="world-card art-world">
          <div class="world-image">
            <img
              :src="artImages[2]?.src || heroArt"
              width="360"
              height="480"
              alt="Kunstwerk mit Natur- und Fantasy-Motiv"
              loading="lazy"
            />
          </div>
          <div class="world-copy">
            <span class="eyebrow">02 / Soul Art</span>
            <h3>Kunstwerke</h3>
            <p>Digital, Acryl & einzigartige Seelenbilder.</p>
            <span class="text-link">Zur Kunst <InkIcon name="diagonal" /></span>
          </div>
        </router-link>
      </div>
    </section>
    <section class="section artist-section">
      <div class="wrap artist-grid">
        <figure class="artist-image">
          <img
            v-if="portrait"
            :src="portrait"
            width="480"
            height="600"
            alt="Rebecca Sophia, die Künstlerin hinter White Pearl Ink"
            loading="lazy"
          />
          <figcaption class="artist-signature">Rebecca Sophia</figcaption>
        </figure>
        <div class="artist-copy">
          <h2>Hi, ich bin <em>Rebecca.</em></h2>
          <p>
            Herzlich willkommen bei White Pearl Ink! Ich bin Rebecca, Künstlerin mit Leidenschaft
            für Tattoos und Malerei. In meinem privaten Tattoostudio in Kreuzlingen am Bodensee
            entstehen vor allem florale Motive, Tier-Tattoos und Fantasy-Designs.
          </p>
          <p>
            Ob individuelles Tattoo oder einzigartiges Gemälde – ich freue mich darauf, deine Ideen
            gemeinsam mit dir zu verwirklichen.
          </p>
          <router-link to="/about-me" class="text-link"
            >Lerne mich kennen <InkIcon name="diagonal"
          /></router-link>
        </div>
      </div>
    </section>
    <section class="section wrap">
      <div class="section-heading">
        <div>
          <span class="eyebrow section-label">Frisch aus dem Studio</span>
          <h2>Latest <em>Ink</em></h2>
        </div>
      </div>
      <div class="gallery-top">
        <div class="segmented" aria-label="Arbeiten auswählen">
          <button :aria-pressed="gallery === 'tattoo'" @click="gallery = 'tattoo'">Tattoos</button
          ><button :aria-pressed="gallery === 'art'" @click="gallery = 'art'">Kunstwerke</button>
        </div>
        <router-link
          :to="gallery === 'tattoo' ? '/tattoos#portfolio' : '/art#portfolio'"
          class="text-link"
          >Alle Arbeiten <InkIcon name="arrow"
        /></router-link>
      </div>
      <GalleryGrid
        :images="gallery === 'tattoo' ? tattooImages : artImages"
        :kind="gallery"
        :loading="gallery === 'tattoo' ? tattoos.loading.value : art.loading.value"
        :error="gallery === 'tattoo' ? tattoos.error.value : art.error.value"
        :initial="4"
        @retry="gallery === 'tattoo' ? tattoos.load() : art.load()"
      />
    </section>
    <ClientReviews />
    <section class="wrap" style="padding-bottom: 80px">
      <div class="studio-teaser">
        <div class="studio-teaser-copy">
          <span class="eyebrow">Location</span>
          <h2>Das <em>Studio</em></h2>
          <p>Dein Safe Space in Kreuzlingen. Ruhe, Kunst & Privatsphäre.</p>
          <p>
            Du kommst aus Konstanz, dem Thurgau oder von weiter weg? Hier findest du die Adresse und
            alle Infos zur Anreise.
          </p>
          <router-link to="/studio" class="text-link"
            >Ein Blick ins Studio <InkIcon name="diagonal"
          /></router-link>
        </div>
        <img
          :src="studioImg"
          width="650"
          height="430"
          alt="Das helle, persönliche White Pearl Ink Studio"
          loading="lazy"
        />
      </div>
    </section>
  </div>
</template>
<script setup>
import { ref, computed } from 'vue';
import { useGallery } from '@/composables/useGallery';
import InkIcon from '@/components/ui/InkIcon.vue';
import GalleryGrid from '@/components/ui/GalleryGrid.vue';
import ClientReviews from '@/components/ui/ClientReviews.vue';
import logo from '@/assets/logo-small.webp';
import studioImg from '@/assets/studio/studio-large.webp';
const prefix = process.env.VUE_APP_API_ORIGIN || '';
const tattoos = useGallery('tattoo_startseite');
const art = useGallery('art_startseite');
const { images: tattooImages } = tattoos;
const { images: artImages } = art;
const { images: portraits } = useGallery('aboutme_selfie');
const portrait = computed(() => portraits.value[0]?.src);
const heroTattoo = computed(
  () => tattooImages.value[0]?.src || prefix + '/galleries/tattoo_startseite/1749282107111.webp'
);
const heroArt = computed(
  () => artImages.value[3]?.src || prefix + '/galleries/art_startseite/1749281938066.webp'
);
const gallery = ref('tattoo');
</script>
